// @flow
import * as React from 'react';
import {useEffect, useState} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import * as Sentry from '@sentry/react';
import {every} from 'lodash';
import ReactFlow, {
  Background,
  useStoreState,
  useZoomPanHelper,
  Controls,
} from 'react-flow-renderer';
import {Body} from 'sail/Text';
import Box from 'sail/Box';

import {useSequenceDiagram} from 'docs/data/diagram';
import {
  positionElements,
  positionSequenceElements,
  hasDimensions,
  getMaxHeight,
  getSegmentWidth,
} from './util';
import {nodeTypes} from './nodeTypes';
import {edgeTypes} from './edgeTypes';
import type {DiagramType, DiagramPositioning} from './types';
import './DiagramContent.scss';

const messages = defineMessages({
  failed: {
    id: 'docsApp.diagram.failed',
    description:
      'An error message to display to the user when a diagram fails to render.',
    defaultMessage:
      'Failed to load diagram that was supposed to show: {description}',
  },
});
const onLoad = (reactFlowInstance) => {
  reactFlowInstance.fitView();
};

type DiagramContentProps = {|
  type: DiagramType,
  positioning: DiagramPositioning,
  description: string,
  initialElements: Array<any>,
  setLoading: (boolean) => void,
|};

export const DiagramContent = ({
  type,
  positioning,
  description,
  initialElements,
  setLoading,
}: DiagramContentProps) => {
  // Internal state provided by react-flow-renderer to let us know the dimensions of the rendered nodes
  const nodes = useStoreState((store) => store.nodes);
  const {fitView} = useZoomPanHelper();
  const {setHeight, setSegmentWidth} = useSequenceDiagram();

  const [buildGraph, setBuildGraph] = useState(true);
  const [fitGraph, setFitGraph] = useState(false);
  const [elements, setElements] = useState(initialElements);
  const [error, setError] = useState(false);

  useEffect(() => {
    // only build the graph if the nodes have been rendered on the page
    // so we know their height and width before calculating their position
    if (buildGraph && nodes?.length && every(nodes, hasDimensions)) {
      try {
        let positionedElements;

        if (positioning === 'auto') {
          if (type === 'sequence') {
            // Get max height and pass to positionSequence
            const maxHeight = getMaxHeight(nodes);
            const segmentWidth = getSegmentWidth(nodes);
            positionedElements = positionSequenceElements(
              initialElements,
              maxHeight,
              segmentWidth,
            );
            setHeight(maxHeight);
            setSegmentWidth(segmentWidth);
          } else {
            positionedElements = positionElements(nodes, initialElements);
          }
        } else {
          // Elements are already positioned with x/y coordinates set on the nodes
          // no need to reposition them
          positionedElements = initialElements;
        }

        if (positionElements.length) {
          setBuildGraph(false);
          setElements(positionedElements);
          setFitGraph(true);
        }
      } catch (e) {
        setElements([]);
        setError(true);
        setLoading(false);
        if (Sentry && Sentry.captureMessage) {
          Sentry.captureMessage(`Failed to build ${type} diagram: ${e}`);
        }
      }
    }
  }, [
    nodes,
    initialElements,
    buildGraph,
    type,
    setLoading,
    positioning,
    setHeight,
    setSegmentWidth,
  ]);

  useEffect(() => {
    if (fitGraph) {
      // Once we position the elements correct, we can center the view
      fitView({padding: 0.3});
      setFitGraph(false);
      setLoading(false);
    }
  }, [fitGraph, fitView, setLoading]);

  const caption = error ? (
    <FormattedMessage {...messages.failed} values={{description}} />
  ) : (
    description
  );

  return (
    <Box divider={{side: 'all'}} style={{padding: '1px', borderRadius: '4px'}}>
      <figure className="DiagramContent">
        <ReactFlow
          elements={elements}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onLoad={onLoad}
          nodesDraggable={false}
          nodesConnectable={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          minZoom={0.4}
        >
          <Background variant="dots" gap={16} size={1} color="#C0C8D2" />
          <Controls showFitView={false} showInteractive={false} />
        </ReactFlow>
        <figcaption
          className={
            error ? 'DiagramContent-Caption' : 'DiagramContent--sr-only'
          }
        >
          <Body>{caption}</Body>
        </figcaption>
      </figure>
    </Box>
  );
};
